import { MainScore } from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import {
    ProductFormProductConsumption,
    ProductFormProductIngredient,
    ProductFormProductPackaging,
    ProductFormProductTransformations,
    ProductFormType,
} from "src/pages/Product/ProductForm/ProductForm.types";
import { getProductIngredientTransportKey } from "./Steps/productIngredientTransportKey";
import { WizardStepKey } from "./useWizardStepsV2";

const SECTION_COMPONENTS: Partial<Record<WizardStepKey, string>> = {
    identity: "questionnaire",
    recipe: "ingredients",
    transformation: "transformations",
    packaging: "packagings",
    transport: "transport",
    distribution: "distribution",
    consumption: "consumption",
};

/** Maps form section keys to the corresponding calculate-preview payload keys. */
export const getPreviewSectionScoreKey = (sectionKey: WizardStepKey): string =>
    sectionKey === "identity" ? "questionnaire" : sectionKey;

/**
 * Ordered life-cycle stages shown in the sidebar. End-of-life is intentionally
 * excluded per the Figma spec.
 */
export type LcaStage = "ingredients" | "transformation" | "packaging" | "transport" | "distribution" | "consumption";

export type LcaStageRow = {
    key: LcaStage;
    labelKey: string;
    defaultLabel: string;
    value: number | null;
    percentage: number | null;
};

export type LcaStageBreakdown = {
    rows: LcaStageRow[];
    total: number | null;
};

const LCA_STAGES: ReadonlyArray<{
    key: LcaStage;
    labelKey: string;
    defaultLabel: string;
    componentId: string | null;
}> = [
    {
        key: "ingredients",
        labelKey: "product.v2.score_live.lca.ingredients",
        defaultLabel: "Ingredients",
        componentId: "ingredients",
    },
    {
        key: "transformation",
        labelKey: "product.v2.score_live.lca.transformation",
        defaultLabel: "Transformation",
        componentId: "transformations",
    },
    {
        key: "packaging",
        labelKey: "product.v2.score_live.lca.packaging",
        defaultLabel: "Packaging",
        componentId: "packagings",
    },
    {
        key: "transport",
        labelKey: "product.v2.score_live.lca.transport",
        defaultLabel: "Transport",
        componentId: "transport",
    },
    {
        key: "distribution",
        labelKey: "product.v2.score_live.lca.distribution",
        defaultLabel: "Distribution",
        componentId: "distribution",
    },
    {
        key: "consumption",
        labelKey: "product.v2.score_live.lca.consumption",
        defaultLabel: "Consumption",
        componentId: "consumption",
    },
];

export const getDefaultDisplayScore = (scores: Record<string, MainScore> | undefined): MainScore | undefined => {
    return Object.values(scores ?? {}).find((item) => item.defaultDisplay);
};

/** Resolves only the selected metric. A missing id must remain unavailable. */
export const resolveMainScoreFromOlap = (
    olapScores: Record<string, MainScore> | undefined,
    scoreId: number,
): MainScore | undefined => {
    return olapScores?.[String(scoreId)];
};

export const getSectionScoreValue = (sectionKey: WizardStepKey, mainScore: MainScore | undefined): number | null => {
    const componentId = SECTION_COMPONENTS[sectionKey];
    if (componentId === undefined) {
        return null;
    }

    const componentsDimension = OlapScoresService.getDimension("components", mainScore?.dimensions);
    const componentScore = OlapScoresService.getDimensionValue(componentId, componentsDimension);

    return OlapScoresService.normalizeNumericalScore(componentScore?.score.value, null);
};

export const getRecipeSectionScoreValue = (
    ingredients: ProductFormProductIngredient[],
    mainScore: MainScore | undefined,
): number | null => {
    const ingredientLineScores = ingredients
        .map((ingredient) => getIngredientLineScoreValue(ingredient, mainScore))
        .filter((score): score is number => score !== null);

    if (ingredientLineScores.length > 0) {
        return ingredientLineScores.reduce((total, lineScore) => total + lineScore, 0);
    }

    return getSectionScoreValue("recipe", mainScore);
};

export const getIngredientLineScoreKey = (ingredient: ProductFormProductIngredient): string | null => {
    if (ingredient.ingredient?.id !== undefined) {
        return ingredient.ingredient.id.toString();
    }

    if (ingredient.intermediateProduct?.id !== undefined) {
        return `intermediate-product-${ingredient.intermediateProduct.id}`;
    }

    if (ingredient.ingredientReference !== undefined) {
        return `ingredient-reference:${ingredient.ingredientReference.source}:${ingredient.ingredientReference.id}`;
    }

    return null;
};

export const getPackagingLineScoreKey = (packaging: ProductFormProductPackaging): string | null => {
    if (packaging.packaging?.id !== undefined) {
        return packaging.packaging.id.toString();
    }

    if (packaging.packagingReference !== undefined) {
        return `reference-${packaging.packagingReference.kind}-${packaging.packagingReference.id}`;
    }

    return null;
};

export const getTransformationLineScoreKey = (transformation: ProductFormProductTransformations): string | null => {
    return `reference-${transformation.transformationReferenceId}`;
};

export const getDistributionLineScoreKey = (distributionReferenceId: number | null | undefined): string | null => {
    if (distributionReferenceId === null || distributionReferenceId === undefined) {
        return null;
    }

    return `reference-${distributionReferenceId}`;
};

export const getConsumptionLineScoreKey = (consumption: ProductFormProductConsumption): string | null => {
    return `reference-${consumption.consumptionReferenceId}`;
};

/**
 * Keys for the `transport_lines` sub-dimension emitted by
 * `App\Olap\Serializer\PostLevelDimension\TransportLines`. The dimension buckets
 * every transport SsqPost under the entity the leg ultimately serves so the
 * inline `ProductTransportInlineSelector` can show a per-row Pts contribution
 * instead of the legacy `Ø` placeholder.
 */
export const getIngredientTransportLineScoreKey = (productIngredientId: number | undefined): string | null => {
    if (productIngredientId === undefined) {
        return null;
    }

    return `pi-${productIngredientId}`;
};

export const getPackagingTransportLineScoreKey = (productPackagingId: number | undefined): string | null => {
    if (productPackagingId === undefined) {
        return null;
    }

    return `pp-${productPackagingId}`;
};

export const PRODUCT_TRANSPORT_LINE_SCORE_KEY = "product" as const;

/** Preview / default circuit line keys emitted as `product-{stage}` by TransportPreviewContributor. */
export const getProductStageTransportLineScoreKey = (stage: string): string => `product-${stage}`;

export const PRODUCT_LEVEL_TRANSPORT_STAGES = [
    "product_to_storage",
    "storage_to_retail",
    "retail_to_consumer",
] as const;

export type ProductLevelTransportStage = (typeof PRODUCT_LEVEL_TRANSPORT_STAGES)[number];

/**
 * Resolves a product-stage transport line score.
 *
 * Default Ecobalyse product distribution posts were historically OLAP-bucketed as
 * `product` before stage-specific keys existed. Only `storage_to_retail` falls
 * back to that legacy key so existing campaigns keep their distribution Pts.
 */
export const resolveProductStageTransportLineValue = (
    stage: ProductLevelTransportStage,
    resolveLineValue: (lineKey: string) => number | null,
): number | null => {
    const stageValue = resolveLineValue(getProductStageTransportLineScoreKey(stage));
    if (stageValue !== null) {
        return stageValue;
    }

    if (stage === "storage_to_retail") {
        return resolveLineValue(PRODUCT_TRANSPORT_LINE_SCORE_KEY);
    }

    return null;
};

/**
 * Sums every transport leg score (ingredients, packaging, product stages) for the
 * Transport section badge. Matches the Ecobalyse preview rollup: normalized ECS
 * per prepared kg values are additive. Falls back to the OLAP `transport`
 * component when no leg score is available.
 */
export const getTransportSectionScoreValue = (
    product: ProductFormType,
    resolveLineValue: (lineKey: string) => number | null,
    mainScore: MainScore | undefined,
): number | null => {
    const values: number[] = [];

    for (const ingredient of product.productIngredients) {
        const persistedKey = getIngredientTransportLineScoreKey(ingredient.id);
        const draftKey = getProductIngredientTransportKey(ingredient);
        const value =
            (persistedKey !== null ? resolveLineValue(persistedKey) : null) ??
            (draftKey !== null ? resolveLineValue(draftKey) : null);

        if (value !== null) {
            values.push(value);
        }
    }

    for (const packaging of product.productPackagings) {
        const key = getPackagingTransportLineScoreKey(packaging.id);
        if (key === null) {
            continue;
        }

        const value = resolveLineValue(key);
        if (value !== null) {
            values.push(value);
        }
    }

    for (const stage of PRODUCT_LEVEL_TRANSPORT_STAGES) {
        const value = resolveProductStageTransportLineValue(stage, resolveLineValue);
        if (value !== null) {
            values.push(value);
        }
    }

    if (values.length > 0) {
        return values.reduce((total, lineValue) => total + lineValue, 0);
    }

    return getSectionScoreValue("transport", mainScore);
};

export const getIngredientLineScoreValue = (
    ingredient: ProductFormProductIngredient,
    mainScore: MainScore | undefined,
): number | null => {
    const lineKey = getIngredientLineScoreKey(ingredient);
    if (lineKey === null) {
        return null;
    }

    return getComponentLineScoreValue(mainScore, "ingredients", "ingredients", lineKey);
};

export const getPackagingLineScoreValue = (
    packaging: ProductFormProductPackaging,
    mainScore: MainScore | undefined,
): number | null => {
    const lineKey = getPackagingLineScoreKey(packaging);
    if (lineKey === null) {
        return null;
    }

    return getComponentLineScoreValue(mainScore, "packagings", "packagings", lineKey);
};

export const getTransformationLineScoreValue = (
    transformation: ProductFormProductTransformations,
    mainScore: MainScore | undefined,
): number | null => {
    const lineKey = getTransformationLineScoreKey(transformation);
    if (lineKey === null) {
        return null;
    }

    return getComponentLineScoreValue(mainScore, "transformations", "transformations", lineKey);
};

export const getDistributionLineScoreValue = (
    distributionReferenceId: number | null | undefined,
    mainScore: MainScore | undefined,
): number | null => {
    const lineKey = getDistributionLineScoreKey(distributionReferenceId);
    if (lineKey === null) {
        return null;
    }

    return getComponentLineScoreValue(mainScore, "distribution", "distribution", lineKey);
};

export const getConsumptionLineScoreValue = (
    consumption: ProductFormProductConsumption,
    mainScore: MainScore | undefined,
): number | null => {
    const lineKey = getConsumptionLineScoreKey(consumption);
    if (lineKey === null) {
        return null;
    }

    return getComponentLineScoreValue(mainScore, "consumption", "consumption", lineKey);
};

export const getTransformationSectionScoreValue = (
    transformations: ProductFormProductTransformations[],
    mainScore: MainScore | undefined,
    fallbackLineScores: Record<string, number | null> = {},
): number | null => {
    const transformationLineScores = transformations
        .map((transformation) => {
            const lineKey = getTransformationLineScoreKey(transformation);
            if (lineKey === null) {
                return null;
            }

            return getTransformationLineScoreValue(transformation, mainScore) ?? fallbackLineScores[lineKey] ?? null;
        })
        .filter((score): score is number => score !== null);

    if (transformationLineScores.length > 0) {
        return transformationLineScores.reduce((total, lineScore) => total + lineScore, 0);
    }

    return getSectionScoreValue("transformation", mainScore);
};

export const getDistributionSectionScoreValue = (
    distributionReferenceId: number | null | undefined,
    mainScore: MainScore | undefined,
    fallbackLineScores: Record<string, number | null> = {},
): number | null => {
    const lineKey = getDistributionLineScoreKey(distributionReferenceId);
    const lineScore =
        lineKey === null ? null : (
            (getDistributionLineScoreValue(distributionReferenceId, mainScore) ?? fallbackLineScores[lineKey] ?? null)
        );

    if (lineScore !== null) {
        return lineScore;
    }

    return getSectionScoreValue("distribution", mainScore);
};

export const getConsumptionSectionScoreValue = (
    consumptions: ProductFormProductConsumption[],
    mainScore: MainScore | undefined,
    fallbackLineScores: Record<string, number | null> = {},
): number | null => {
    const consumptionLineScores = consumptions
        .map((consumption) => {
            const lineKey = getConsumptionLineScoreKey(consumption);
            if (lineKey === null) {
                return null;
            }

            return getConsumptionLineScoreValue(consumption, mainScore) ?? fallbackLineScores[lineKey] ?? null;
        })
        .filter((score): score is number => score !== null);

    if (consumptionLineScores.length > 0) {
        return consumptionLineScores.reduce((total, lineScore) => total + lineScore, 0);
    }

    return getSectionScoreValue("consumption", mainScore);
};

export const getTransportLineScoreValue = (lineKey: string | null, mainScore: MainScore | undefined): number | null => {
    if (lineKey === null) {
        return null;
    }

    return getComponentLineScoreValue(mainScore, "transport", "transport_lines", lineKey);
};

const getComponentLineScoreValue = (
    mainScore: MainScore | undefined,
    componentId: string,
    lineDimensionName: string,
    lineKey: string,
): number | null => {
    const componentsDimension = OlapScoresService.getDimension("components", mainScore?.dimensions);
    const componentScore = OlapScoresService.getDimensionValue(componentId, componentsDimension);
    const lineDimension = OlapScoresService.getDimension(lineDimensionName, componentScore?.dimensions);
    const lineScore = OlapScoresService.getDimensionValue(lineKey, lineDimension);

    return OlapScoresService.normalizeNumericalScore(lineScore?.score.value, null);
};

/**
 * Returns the LCA stage breakdown for the score sidebar.
 *
 * Every row is read from the `components` OLAP dimension so the left section
 * badges and right score summary stay aligned.
 */
export const getLcaStageBreakdown = (mainScore: MainScore | undefined): LcaStageBreakdown => {
    const total = OlapScoresService.normalizeNumericalScore(mainScore?.score.value, null);
    const componentsDimension = OlapScoresService.getDimension("components", mainScore?.dimensions);

    const rows = LCA_STAGES.map((stage): LcaStageRow => {
        const value =
            stage.componentId === null ?
                null
            :   OlapScoresService.normalizeNumericalScore(
                    OlapScoresService.getDimensionValue(stage.componentId, componentsDimension)?.score.value,
                    null,
                );
        const percentage =
            value === null || total === null ? null
            : total === 0 ? 0
            : Math.min(100, Math.max(0, (value / total) * 100));

        return {
            key: stage.key,
            labelKey: stage.labelKey,
            defaultLabel: stage.defaultLabel,
            value,
            percentage,
        };
    });

    return { rows, total };
};
